import { container } from '@/di/container';
import { Repuesto } from '@/domain/entities/repuesto';
import { IRepuestoRepository } from '@/domain/repositories/IRepuestoRepository';
import { GetRepuestosUseCase } from '@/application/use-cases/repuestos/GetRepuestosUseCase';
import { PostRepuestoUseCase } from '@/application/use-cases/repuestos/PostRepuestoUseCase';
import { PatchRepuestoUseCase } from '@/application/use-cases/repuestos/PatchRepuestoUseCase';
import { CreateRepuestosDTO } from '@/application/dto/repuesto/CreateRepuestosDTO';
import { ChangeRepuestosDTO } from '@/application/dto/repuesto/ChangeRepuestosDTO';
import { useCallback, useEffect, useState } from 'react';

export function useRepuesto(id?: string) {
  const [data, setData] = useState<Repuesto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const repository =
    container.get<IRepuestoRepository>(
      'IRepuestoRepository'
    );

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);
      const result = await container
        .get<GetRepuestosUseCase>('GetRepuestosUseCase')
        .execute();
      setData(result);
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar los repuestos');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(undefined);
      const result = await repository.findById(id);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar los repuestos');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [id, repository]);
  const create = async (
    data: CreateRepuestosDTO
  ) => {
    try {
      setLoading(true);
      setError(undefined);
      return await container.get<PostRepuestoUseCase>('PostRepuestoUseCase').execute(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear el repuesto');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const changeEstado = useCallback(async (
    repuestoId: string | number,
    changes: ChangeRepuestosDTO
  ) => {
    try {
      setLoading(true);
      setError(undefined);
      const estado = Number(changes.estado);
      await container.get<PatchRepuestoUseCase>('PatchRepuestoUseCase')
        .execute(String(repuestoId), { estado });
      setData(current => current.map(item =>
        String(item.id) === String(repuestoId) ? { ...item, estado } : item
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cambiar el estado del repuesto');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // El error de la carga automática queda disponible en el estado del hook.
    void load().catch(() => {});
  }, [load]);

  return {
    data,
    loading,
    error,
    reload: load,
    loadAll,
    create,
    changeEstado
  };
}
